import React, { useState } from 'react'
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  VStack,
  HStack,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Switch,
  FormErrorMessage,
  FormHelperText,
  useToast,
  SimpleGrid,
  Select,
  Box,
  Text,
} from '@chakra-ui/react'
import { FiPlus } from 'react-icons/fi'
import apiService from '@/services/api'

interface CreatePlanModalProps {
  isOpen: boolean
  onClose: () => void
  onSuccess: () => void
}

export default function CreatePlanModal({ isOpen, onClose, onSuccess }: CreatePlanModalProps) {
  const toast = useToast()
  const [loading, setLoading] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    description: '',
    type: 'preset' as 'preset' | 'custom',
    is_active: true,
    file_limit: 1000,
    smart_tagging_limit: 100,
    smart_renaming_limit: 100,
    chatbot_queries_limit: 500,
    storage_limit_gb: 10,
    users_limit: 5,
    price_monthly: 0,
    price_yearly: 0,
  })

  const handleInputChange = (field: string, value: any) => {
    setFormData({ ...formData, [field]: value })
    if (errors[field]) {
      setErrors({ ...errors, [field]: '' })
    }
  }

  const validateForm = () => {
    const newErrors: Record<string, string> = {}

    if (!formData.name.trim()) {
      newErrors.name = 'Plan name is required'
    }

    if (formData.file_limit <= 0) {
      newErrors.file_limit = 'Page limit must be greater than 0'
    }

    if (formData.smart_tagging_limit <= 0) {
      newErrors.smart_tagging_limit = 'Smart tagging limit must be greater than 0'
    }

    if (formData.smart_renaming_limit <= 0) {
      newErrors.smart_renaming_limit = 'Smart renaming limit must be greater than 0'
    }

    if (formData.chatbot_queries_limit <= 0) {
      newErrors.chatbot_queries_limit = 'Chatbot queries limit must be greater than 0'
    }

    if (formData.storage_limit_gb <= 0) {
      newErrors.storage_limit_gb = 'Storage limit must be greater than 0'
    }

    if (formData.users_limit <= 0) {
      newErrors.users_limit = 'Users limit must be greater than 0'
    }

    if (formData.price_monthly < 0) {
      newErrors.price_monthly = 'Price cannot be negative'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async () => {
    if (!validateForm()) return

    try {
      setLoading(true)
      await apiService.createPlan(formData)

      toast({
        title: 'Plan created',
        description: `${formData.name} has been created successfully`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      })

      onSuccess()
      handleClose()
    } catch (error: any) {
      toast({
        title: 'Error creating plan',
        description: error.response?.data?.message || 'Failed to create plan',
        status: 'error',
        duration: 5000,
        isClosable: true,
      })
    } finally {
      setLoading(false)
    }
  }

  const handleClose = () => {
    setFormData({
      name: '',
      slug: '',
      description: '',
      type: 'preset',
      is_active: true,
      file_limit: 1000,
      smart_tagging_limit: 100,
      smart_renaming_limit: 100,
      chatbot_queries_limit: 500,
      storage_limit_gb: 10,
      users_limit: 5,
      price_monthly: 0,
      price_yearly: 0,
    })
    setErrors({})
    onClose()
  }

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="lg">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Create New Plan</ModalHeader>
        <ModalCloseButton />

        <ModalBody>
          <VStack spacing={4} align="stretch">
            {/* Basic Information */}
            <Box>
              <Text fontSize="md" fontWeight="semibold" mb={3} color="gray.800">
                Basic Information
              </Text>

              <SimpleGrid columns={2} spacing={4}>
                <FormControl isRequired isInvalid={!!errors.name}>
                  <FormLabel>Plan Name</FormLabel>
                  <Input
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    placeholder="e.g., Professional"
                  />
                  <FormErrorMessage>{errors.name}</FormErrorMessage>
                </FormControl>

                <FormControl>
                  <FormLabel>Slug</FormLabel>
                  <Input
                    value={formData.slug}
                    onChange={(e) => handleInputChange('slug', e.target.value)}
                    placeholder="e.g., professional (auto-generated if empty)"
                  />
                  <FormHelperText>Leave empty for auto-generation</FormHelperText>
                </FormControl>
              </SimpleGrid>

              <FormControl mt={4}>
                <FormLabel>Description</FormLabel>
                <Textarea
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  placeholder="Describe what this plan offers"
                  rows={3}
                />
              </FormControl>

              <SimpleGrid columns={2} spacing={4} mt={4}>
                <FormControl>
                  <FormLabel>Plan Type</FormLabel>
                  <Select
                    value={formData.type}
                    onChange={(e) => handleInputChange('type', e.target.value)}
                  >
                    <option value="preset">Preset Plan</option>
                    <option value="custom">Custom Plan</option>
                  </Select>
                </FormControl>

                <FormControl>
                  <FormLabel>Status</FormLabel>
                  <HStack>
                    <Switch
                      isChecked={formData.is_active}
                      onChange={(e) => handleInputChange('is_active', e.target.checked)}
                    />
                    <Text fontSize="sm">{formData.is_active ? 'Active' : 'Inactive'}</Text>
                  </HStack>
                </FormControl>
              </SimpleGrid>
            </Box>

            {/* Limits */}
            <Box>
              <Text fontSize="md" fontWeight="semibold" mb={3} color="gray.800">
                Resource Limits
              </Text>

              <SimpleGrid columns={2} spacing={4}>
                <FormControl isInvalid={!!errors.file_limit}>
                  <FormLabel>Page Limit</FormLabel>
                  <NumberInput
                    value={formData.file_limit}
                    onChange={(valueString, valueNumber) =>
                      handleInputChange('file_limit', valueNumber || 0)
                    }
                    min={1}
                  >
                    <NumberInputField />
                    <NumberInputStepper>
                      <NumberIncrementStepper />
                      <NumberDecrementStepper />
                    </NumberInputStepper>
                  </NumberInput>
                  <FormErrorMessage>{errors.file_limit}</FormErrorMessage>
                </FormControl>

                <FormControl isInvalid={!!errors.users_limit}>
                  <FormLabel>Users Limit</FormLabel>
                  <NumberInput
                    value={formData.users_limit}
                    onChange={(valueString, valueNumber) =>
                      handleInputChange('users_limit', valueNumber || 0)
                    }
                    min={1}
                  >
                    <NumberInputField />
                    <NumberInputStepper>
                      <NumberIncrementStepper />
                      <NumberDecrementStepper />
                    </NumberInputStepper>
                  </NumberInput>
                  <FormErrorMessage>{errors.users_limit}</FormErrorMessage>
                </FormControl>

                <FormControl isInvalid={!!errors.smart_tagging_limit}>
                  <FormLabel>Smart Tagging (Monthly)</FormLabel>
                  <NumberInput
                    value={formData.smart_tagging_limit}
                    onChange={(valueString, valueNumber) =>
                      handleInputChange('smart_tagging_limit', valueNumber || 0)
                    }
                    min={1}
                  >
                    <NumberInputField />
                    <NumberInputStepper>
                      <NumberIncrementStepper />
                      <NumberDecrementStepper />
                    </NumberInputStepper>
                  </NumberInput>
                  <FormErrorMessage>{errors.smart_tagging_limit}</FormErrorMessage>
                </FormControl>

                <FormControl isInvalid={!!errors.smart_renaming_limit}>
                  <FormLabel>Smart Renaming (Monthly)</FormLabel>
                  <NumberInput
                    value={formData.smart_renaming_limit}
                    onChange={(valueString, valueNumber) =>
                      handleInputChange('smart_renaming_limit', valueNumber || 0)
                    }
                    min={1}
                  >
                    <NumberInputField />
                    <NumberInputStepper>
                      <NumberIncrementStepper />
                      <NumberDecrementStepper />
                    </NumberInputStepper>
                  </NumberInput>
                  <FormErrorMessage>{errors.smart_renaming_limit}</FormErrorMessage>
                </FormControl>

                <FormControl isInvalid={!!errors.chatbot_queries_limit}>
                  <FormLabel>Chatbot Queries (Monthly)</FormLabel>
                  <NumberInput
                    value={formData.chatbot_queries_limit}
                    onChange={(valueString, valueNumber) =>
                      handleInputChange('chatbot_queries_limit', valueNumber || 0)
                    }
                    min={1}
                  >
                    <NumberInputField />
                    <NumberInputStepper>
                      <NumberIncrementStepper />
                      <NumberDecrementStepper />
                    </NumberInputStepper>
                  </NumberInput>
                  <FormErrorMessage>{errors.chatbot_queries_limit}</FormErrorMessage>
                </FormControl>

                <FormControl isInvalid={!!errors.storage_limit_gb}>
                  <FormLabel>Storage Limit (GB)</FormLabel>
                  <NumberInput
                    value={formData.storage_limit_gb}
                    onChange={(valueString, valueNumber) =>
                      handleInputChange('storage_limit_gb', valueNumber || 0)
                    }
                    min={1}
                  >
                    <NumberInputField />
                    <NumberInputStepper>
                      <NumberIncrementStepper />
                      <NumberDecrementStepper />
                    </NumberInputStepper>
                  </NumberInput>
                  <FormErrorMessage>{errors.storage_limit_gb}</FormErrorMessage>
                </FormControl>
              </SimpleGrid>
            </Box>

            {/* Pricing */}
            <Box>
              <Text fontSize="md" fontWeight="semibold" mb={3} color="gray.800">
                Pricing (Optional)
              </Text>

              <SimpleGrid columns={2} spacing={4}>
                <FormControl isInvalid={!!errors.price_monthly}>
                  <FormLabel>Monthly Price ($)</FormLabel>
                  <NumberInput
                    value={formData.price_monthly}
                    onChange={(valueString, valueNumber) =>
                      handleInputChange('price_monthly', valueNumber || 0)
                    }
                    min={0}
                    precision={2}
                  >
                    <NumberInputField />
                    <NumberInputStepper>
                      <NumberIncrementStepper />
                      <NumberDecrementStepper />
                    </NumberInputStepper>
                  </NumberInput>
                  <FormHelperText>Set to 0 for free plans</FormHelperText>
                  <FormErrorMessage>{errors.price_monthly}</FormErrorMessage>
                </FormControl>

                <FormControl>
                  <FormLabel>Yearly Price ($)</FormLabel>
                  <NumberInput
                    value={formData.price_yearly}
                    onChange={(valueString, valueNumber) =>
                      handleInputChange('price_yearly', valueNumber || 0)
                    }
                    min={0}
                    precision={2}
                  >
                    <NumberInputField />
                    <NumberInputStepper>
                      <NumberIncrementStepper />
                      <NumberDecrementStepper />
                    </NumberInputStepper>
                  </NumberInput>
                  <FormHelperText>Optional yearly pricing</FormHelperText>
                </FormControl>
              </SimpleGrid>
            </Box>
          </VStack>
        </ModalBody>

        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={handleClose}>
            Cancel
          </Button>
          <Button
            leftIcon={<FiPlus />}
            colorScheme="blue"
            onClick={handleSubmit}
            isLoading={loading}
            loadingText="Creating..."
          >
            Create Plan
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
